import { useCallback } from 'react';
import useAxiosInstance from './axiosInstance';

/**
 * Custom hook to upload files to the server.
 * @param {function} navigate - Function to navigate to another page.
 * @returns {object} Object containing uploadFile and uploadFiles functions.
 */
const useFileUpload = (navigate, container_name=null) => {
  const axiosInstance = useAxiosInstance(navigate);

  const uploadFile = useCallback(async (file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axiosInstance.post(container_name ? `/upload?container=${container_name}` : '/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onUploadProgress) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onUploadProgress(percentCompleted);
          }
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading file: ${file.name}`, error);
      throw error;
    }
  }, [axiosInstance, container_name]);

  const uploadFiles = useCallback(async (files, onProgress, batchSize = 5) => {
    const totalFiles = files.length;
    let uploadedFiles = 0;

    const updateProgress = () => {
      const percentCompleted = Math.round((uploadedFiles / totalFiles) * 100);
      if (onProgress) onProgress(percentCompleted);
    };

    const uploadBatch = async (batch) => {
      await Promise.all(
        batch.map(async (file) => {
          try {
            await uploadFile(file, updateProgress);
            uploadedFiles += 1;
            updateProgress();
          } catch (error) {
            console.error(`Error uploading file: ${file.name}`, error);
          }
        })
      );
    };

    for (let i = 0; i < files.length; i += batchSize) {
      const batch = files.slice(i, i + batchSize);
      await uploadBatch(batch);
    }
  }, [uploadFile]);

  return { uploadFile, uploadFiles };
};

export default useFileUpload;